/**
 * Application header with navigation controls and chapter title.
 */

import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from './state/bible-state';
import {
  formatChapterReference,
  formatAbbrevChapterReference,
  getNextChapter,
  getPreviousChapter,
} from '@skjv/common';
import { TableOfContents } from '@components/table-of-contents';
import { GetAppModal } from '@components/get-app/get-app-modal';
import { isDesktop } from '@/desktop/desktop';
import {
  HamburgerIcon,
  BookIcon,
  DownloadIcon,
  StudyIcon,
  SparklesIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@components/icons';

interface AppHeaderProps {
  displayedReference: string;
  abbreviatedReference: string;
  isChapterChanged: boolean;
  onReferenceUpdate: (full: string, abbrev: string) => void;
}

export const AppHeader = observer(function AppHeader({
  displayedReference,
  abbreviatedReference,
  isChapterChanged,
  onReferenceUpdate,
}: AppHeaderProps) {
  const [isTocOpen, setIsTocOpen] = useState(false);
  // Closing the study panel pulses its header icon three times so the eye
  // follows where the studies went (desktop UX, UAT 2026-08-27).
  const [isStudyPulsing, setIsStudyPulsing] = useState(false);
  const [isGetAppOpen, setIsGetAppOpen] = useState(false);
  const studyCloseNonce = bibleState.studyCloseNonce;
  useEffect(() => {
    if (studyCloseNonce === 0) return;
    setIsStudyPulsing(true);
    const timer = setTimeout(() => setIsStudyPulsing(false), 1300);
    return () => clearTimeout(timer);
  }, [studyCloseNonce]);
  const [selectedTestament, setSelectedTestament] = useState<0 | 1>(0);
  const [selectedBookIndex, setSelectedBookIndex] = useState<number | null>(null);
  const [selectedChapterIndex, setSelectedChapterIndex] = useState<number | null>(null);

  const prevTarget = getPreviousChapter(bibleState.currentLocation);
  const nextTarget = getNextChapter(bibleState.currentLocation);

  const goToChapter = (target: typeof prevTarget) => {
    if (!target) return;
    bibleState.setCurrentLocation(target);
    onReferenceUpdate(
      formatChapterReference(target),
      formatAbbrevChapterReference(target),
    );
  };

  return (
    <>
      <header className="app-header">
        <div className="header-content">
          {/* Left: Study button on desktop; the website offers the app
              instead — the Study system is native-only (DESKTOP-PRD §3.8) */}
          {isDesktop() ? (
            <button
              onClick={() => bibleState.showStudyPanel()}
              className={`header-btn header-btn-secondary p-2 text-stone-600 hover:text-stone-800 dark:text-stone-300 dark:hover:text-stone-100 ${!bibleState.hasSeenGuide && !bibleState.hasAnyGroup() ? 'new-user-pulse' : ''} ${isStudyPulsing ? 'study-close-pulse' : ''}`}
              title="Bible Studies"
            >
              <StudyIcon className="h-6 w-6" />
            </button>
          ) : (
            // Invisible (not removed) on phones so the centered title keeps its slot
            <button
              onClick={() => setIsGetAppOpen(true)}
              className="header-btn header-btn-secondary flex items-center max-sm:invisible gap-1.5 p-2 text-sm font-medium text-stone-600 hover:text-stone-800 dark:text-stone-300 dark:hover:text-stone-100"
              title="Get the App"
            >
              <DownloadIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Get the App</span>
            </button>
          )}

          {/* Center: Chapter title */}
          <div className="flex flex-1 items-center justify-center gap-1">
            <button
              className={`app-header-title flex items-center gap-3 px-4 ${isChapterChanged ? 'chapter-changed' : ''} ${isTocOpen ? 'active' : ''}`}
              onClick={() => {
                if (!isTocOpen) {
                  setSelectedTestament(bibleState.currentLocation.testamentIndex as 0 | 1);
                }
                setIsTocOpen(!isTocOpen);
                setSelectedBookIndex(null);
                setSelectedChapterIndex(null);
              }}
              title="Open Table of Contents"
            >
              <span className="header-ref-full">{displayedReference}</span>
              <span className="header-ref-abbrev">{abbreviatedReference}</span>
              <BookIcon className="h-7 w-7" />
            </button>
            <button
              onClick={() => goToChapter(prevTarget)}
              disabled={!prevTarget}
              className="header-btn p-1 text-stone-600 hover:text-stone-800 disabled:opacity-30 dark:text-stone-300 dark:hover:text-stone-100"
              title="Previous chapter"
              aria-label="Previous chapter"
            >
              <ChevronLeftIcon className="h-6 w-6" />
            </button>
            <button
              onClick={() => goToChapter(nextTarget)}
              disabled={!nextTarget}
              className="header-btn p-1 text-stone-600 hover:text-stone-800 disabled:opacity-30 dark:text-stone-300 dark:hover:text-stone-100"
              title="Next chapter"
              aria-label="Next chapter"
            >
              <ChevronRightIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Right: AI chat + Hamburger */}
          <div className="flex items-center gap-1">
            <button
              data-testid="chat-toggle"
              onClick={() =>
                bibleState.chatPanelVisible
                  ? bibleState.hideChatPanel()
                  : bibleState.showChatPanel()
              }
              className="header-btn header-btn-secondary p-2 text-stone-600 hover:text-stone-800 dark:text-stone-300 dark:hover:text-stone-100"
              title={bibleState.chatPanelVisible ? 'Close panel' : 'It is written...'}
            >
              <SparklesIcon className="h-6 w-6" />
            </button>
            <button
              onClick={() => {
                if (bibleState.userDataPanelVisible) {
                  bibleState.hideUserDataPanel();
                } else {
                  bibleState.showUserDataPanel(bibleState.userDataPanelTab);
                }
                setIsTocOpen(false);
                setSelectedBookIndex(null);
                setSelectedChapterIndex(null);
              }}
              className="header-btn p-2 text-stone-600 hover:text-stone-800 dark:text-stone-300 dark:hover:text-stone-100"
              title={bibleState.userDataPanelVisible ? 'Close menu' : 'Open menu'}
            >
              <HamburgerIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Get the App (web only) */}
      {isGetAppOpen && <GetAppModal onClose={() => setIsGetAppOpen(false)} />}

      {/* Table of Contents Overlay */}
      <TableOfContents
        isOpen={isTocOpen}
        selectedTestament={selectedTestament}
        selectedBookIndex={selectedBookIndex}
        selectedChapterIndex={selectedChapterIndex}
        onTestamentChange={setSelectedTestament}
        onBookSelect={(bookIndex) => {
          setSelectedBookIndex(bookIndex);
          setSelectedChapterIndex(null);
        }}
        onChapterSelect={setSelectedChapterIndex}
        onNavigate={(testamentIndex, bookIndex, chapterIndex, verseIndex) => {
          const newLocation = { testamentIndex, bookIndex, chapterIndex, verseIndex };
          bibleState.setCurrentLocation(newLocation);
          onReferenceUpdate(
            formatChapterReference(newLocation),
            formatAbbrevChapterReference(newLocation),
          );
          setIsTocOpen(false);
          setSelectedBookIndex(null);
          setSelectedChapterIndex(null);
        }}
        onClose={() => {
          setIsTocOpen(false);
          setSelectedBookIndex(null);
          setSelectedChapterIndex(null);
        }}
      />
    </>
  );
});
